import clsx from "clsx";
import { differenceInMinutes } from "date-fns";

import UserAvatar from "@/containers/UserAvatar";
import { useAppSelector } from "@/hooks";
import { sessionSelector } from "@/store/sessionReducer";
import { parsedDate } from "@/utils/dates";

export type UserLikeType = {
  id: string;
  username: string;
  customerId: string;
  avatar: UserAvatar;

  contact?: Nullable<{
    displayName: string;
  }>;

  lastSeenAt: Nilable<string>;
  homeoffice: boolean;
};

type UserProps = {
  user: Nilable<UserLikeType>;
  withUsername?: boolean;
  withHomeOfficeStatus?: boolean;
  className?: string;
  containerClassName?: string;
};

const displayNameForUser = (user: UserLikeType, withUsername: boolean = false, isAnonymous: boolean) => {
  if (isAnonymous) {
    return [`Benutzer #${user.id}`];
  }

  let name: string;
  if (user.contact) {
    name = user.contact.displayName;

    if (withUsername) {
      name += ` (${user.username})`;
    }
  } else {
    name = user.username;
  }

  return name.split(/\s+/);
};

export default function User({ user, withUsername, withHomeOfficeStatus, className, containerClassName }: UserProps) {
  const { currentUser, currentProject } = useAppSelector(sessionSelector);

  if (!user) return null;

  const isAnonymous =
    currentUser.customerId !== "1" && user.customerId === "1" && (currentProject.attrs?.anonymize ?? true);
  const [firstPart, ...lastPart] = displayNameForUser(user, withUsername, isAnonymous);
  const avatar = isAnonymous
    ? {
        __typename: "UserAvatar" as const,
        thumb: "/avatar-anonymous.svg",
        medium: "/avatar-anonymous.svg",
        original: "/avatar-anonymous.svg",
      }
    : user.avatar;

  const isOnline = user.lastSeenAt
    ? differenceInMinutes(new Date(), parsedDate(user.lastSeenAt)) <= 10 && !isAnonymous
    : undefined;

  return (
    <span className={clsx(containerClassName)}>
      <span className={clsx("TT-user", "redacted", className, { "is-online": isOnline })}>
        <span className="TT-user-first-part">
          <UserAvatar type="thumb" alt="Benutzer" avatar={avatar} /> {firstPart}
        </span>{" "}
        {lastPart.join(" ")}
      </span>
      {withHomeOfficeStatus && user.homeoffice && <span className="TT-user-homeoffice">Home-Office</span>}
    </span>
  );
}
